import { useState, useMemo, useCallback } from "react";
import { useClients } from "@/hooks/useClients";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { useDeleteClient } from "@/hooks/useDeleteClient";
import { useCompanies } from "@/hooks/useCompanies";
import { useClientStatusOptions } from "@/hooks/useClientStatusOptions";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { ClientDialog } from "@/components/clients/ClientDialog";
import { ClientAdvancedFilter } from "@/components/clients/ClientAdvancedFilter";
import { ClientStatusStats } from "@/components/clients/ClientStatusStats";
import { PaginationControls } from "@/components/clients/PaginationControls";
import { Client } from "@/types/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { RefreshCw, Plus, Edit, Trash2, ChevronDown, ChevronUp, User, FileText, Building2, Paperclip, Save, Files, Eye, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { ClientInlineFilter } from "@/components/clients/ClientInlineFilter";
import { toast } from "sonner";
import { ClientInfoInputs } from "@/components/clients/ClientDialog/ClientInfoInputs";
import { CompanySelector } from "@/components/clients/ClientDialog/CompanySelector";
import { AdditionalInfoTab } from "@/components/clients/ClientDialog/AdditionalInfoTab";
import { PassportInfoTab } from "@/components/clients/ClientDialog/PassportInfoTab";
import { AttachmentsTab } from "@/components/clients/ClientDialog/AttachmentsTab";
import { ClientEditDialog } from "@/components/clients/ClientEditDialog";
import { ClientFilesDialog } from "@/components/clients/ClientFilesDialog";
import { ClientViewDialog } from "@/components/clients/ClientViewDialog";
type FormData = Omit<Client, "id" | "created_at" | "updated_at" | "client_assignments">;
const getInitialFormData = (): FormData => ({
  name_ar: "",
  name_en: "",
  mobile: "",
  additional_info: "",
  passport_info: {},
  attachments: [],
  status: "waiting",
  client_type: "direct",
  company_id: undefined,
  company: "",
  created_by: "",
  creator: undefined,
  companies: undefined,
  employees: undefined
});
export default function DirectClients() {
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<FormData>(getInitialFormData);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>();
  const [isFilesDialogOpen, setIsFilesDialogOpen] = useState(false);
  const [filesDialogClient, setFilesDialogClient] = useState<Client | undefined>();
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingClient, setViewingClient] = useState<Client | undefined>();
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [filters, setFilters] = useState({
    name_ar: '',
    name_en: '',
    mobile: '',
    company: '',
    status: '',
    generalSearch: '',
    created_at_from: '',
    created_at_to: ''
  });
  const {
    data: clients = [],
    isLoading,
    error,
    refetch
  } = useClients();
  const {
    data: companies = []
  } = useCompanies();
  const {
    statusOptions
  } = useClientStatusOptions();
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();

  // تصفية العملاء المباشرين فقط
  const directClients = useMemo(() => {
    return clients.filter(client => !client.client_type || client.client_type === 'direct');
  }, [clients]);

  // تطبيق الفلاتر على العملاء المباشرين
  const filteredDirectClients = useMemo(() => {
    let filtered = directClients;

    // البحث العام
    if (filters.generalSearch) {
      const searchTerm = filters.generalSearch.toLowerCase();
      filtered = filtered.filter(client => client.name_ar?.toLowerCase().includes(searchTerm) || client.name_en?.toLowerCase().includes(searchTerm) || client.mobile?.toLowerCase().includes(searchTerm) || client.company?.toLowerCase().includes(searchTerm));
    }

    // فلترة بالاسم العربي
    if (filters.name_ar) {
      filtered = filtered.filter(client => client.name_ar?.toLowerCase().includes(filters.name_ar.toLowerCase()));
    }

    // فلترة بالاسم الإنجليزي
    if (filters.name_en) {
      filtered = filtered.filter(client => client.name_en?.toLowerCase().includes(filters.name_en.toLowerCase()));
    }

    // فلترة برقم الجوال
    if (filters.mobile) {
      filtered = filtered.filter(client => client.mobile?.includes(filters.mobile));
    }

    // فلترة بالشركة
    if (filters.company) {
      filtered = filtered.filter(client => client.company?.toLowerCase().includes(filters.company.toLowerCase()));
    }

    // فلترة بالحالة
    if (filters.status) {
      filtered = filtered.filter(client => client.status === filters.status);
    }

    // فلترة بتاريخ الإنشاء من
    if (filters.created_at_from) {
      filtered = filtered.filter(client => {
        const clientDate = new Date(client.created_at);
        const fromDate = new Date(filters.created_at_from);
        return clientDate >= fromDate;
      });
    }

    // فلترة بتاريخ الإنشاء إلى
    if (filters.created_at_to) {
      filtered = filtered.filter(client => {
        const clientDate = new Date(client.created_at);
        const toDate = new Date(filters.created_at_to);
        toDate.setHours(23, 59, 59, 999); // نهاية اليوم
        return clientDate <= toDate;
      });
    }
    return filtered;
  }, [directClients, filters]);
  const handleAddFormChange = useCallback((field: string, value: any) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  const handleAddFormSubmit = useCallback(async () => {
    try {
      await createClientMutation.mutateAsync({
        ...addFormData,
        client_type: 'direct'
      });
      toast.success("تم إضافة العميل المباشر بنجاح");
      setAddFormData(getInitialFormData());
      setIsAddFormOpen(false);
      setActiveTab("basic");
    } catch (error) {
      console.error("Error creating direct client:", error);
      toast.error("حدث خطأ أثناء إضافة العميل");
    }
  }, [addFormData, createClientMutation]);
  const handleCompanyAdded = useCallback((newCompany: any) => {
    setAddFormData(prev => ({
      ...prev,
      company_id: newCompany.id,
      company: newCompany.name_ar
    }));
  }, []);
  const handleCompanyChange = useCallback((companyId: string | undefined) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    setAddFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: selectedCompany?.name_ar || ""
    }));
  }, [companies]);
  const handleEdit = useCallback((client: Client) => {
    setEditingClient(client);
    setIsEditDialogOpen(true);
  }, []);
  const handleViewFiles = useCallback((client: Client) => {
    setFilesDialogClient(client);
    setIsFilesDialogOpen(true);
  }, []);
  const handleViewClient = useCallback((client: Client) => {
    setViewingClient(client);
    setIsViewDialogOpen(true);
  }, []);
  const handleDelete = useCallback(async (client: Client) => {
    if (window.confirm(`هل أنت متأكد من حذف العميل ${client.name_ar}؟`)) {
      try {
        await deleteClientMutation.mutateAsync(client.id);
        toast.success("تم حذف العميل بنجاح");
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error("حدث خطأ أثناء حذف العميل");
      }
    }
  }, [deleteClientMutation]);
  const handleSuccess = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedClient(undefined);
    refetch();
  }, [refetch]);
  const columns: TableColumn<Client>[] = [{
    key: "name_ar",
    title: "الاسم بالعربية",
    render: client => <div className="font-medium">{client.name_ar}</div>
  }, {
    key: "name_en",
    title: "الاسم بالإنجليزية",
    render: client => <div className="text-muted-foreground">{client.name_en || "-"}</div>
  }, {
    key: "mobile",
    title: "رقم الجوال",
    render: client => <div className="text-muted-foreground">{client.mobile || "-"}</div>
  }, {
    key: "company",
    title: "الشركة",
    render: client => <div className="text-muted-foreground">{client.company || "ترافليون"}</div>
  }, {
    key: "status",
    title: "الحالة",
    render: client => <Badge variant="outline">{client.status}</Badge>
  }, {
    key: "actions",
    title: "الإجراءات",
    align: 'center',
    render: client => <div className="flex gap-2 justify-center">
        <Button variant="ghost" size="sm" onClick={() => handleViewClient(client)} className="h-8 w-8 p-0 text-gray-600 hover:text-gray-800 hover:bg-gray-100" title="عرض">
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100" title="تعديل">
          <Edit className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleViewFiles(client)} className="h-8 w-8 p-0 text-green-600 hover:text-green-800 hover:bg-green-100" title="الملفات">
          <Files className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleDelete(client)} className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-100" title="حذف">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
  }];
  if (isLoading) {
    return <LoadingSpinnerWithText text="جاري تحميل العملاء المباشرين..." />;
  }
  if (error) {
    return <div className="text-center text-red-500">
        حدث خطأ أثناء تحميل العملاء: {error.message}
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6">
      {/* Header with controls */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">العملاء المباشرين</h1>
        
        {/* زر الإضافة */}
        <Collapsible open={isAddFormOpen} onOpenChange={setIsAddFormOpen}>
          <CollapsibleTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة عميل مباشر
              {isAddFormOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
        
        {/* الفلتر المتقدم */}
        <ClientInlineFilter onFilterChange={setFilters} statusOptions={Object.entries(statusOptions || {}).map(([key, option]) => ({
        value: key,
        label: option.label,
        color: option.color
      }))} currentFilters={filters} />
        
        {/* أزرار العرض */}
        <ViewToggle view={view} onViewChange={setView} />
        
        {/* البحث السريع */}
        <div className="relative flex-1 mr-auto">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="البحث السريع..." value={filters.generalSearch} onChange={e => setFilters({
          ...filters,
          generalSearch: e.target.value
        })} className="pr-10" />
        </div>
      </div>

      <Card>
        <CardContent className="pb-0">
          <OptimizedTable 
            data={filteredDirectClients.slice((currentPage - 1) * pageSize, currentPage * pageSize)} 
            columns={columns} 
          />
        </CardContent>
        <div className="px-6 pb-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={Math.ceil(filteredDirectClients.length / pageSize)}
            pageSize={pageSize}
            totalItems={filteredDirectClients.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </Card>

      <ClientDialog client={selectedClient} open={isDialogOpen} onOpenChange={setIsDialogOpen} onSuccess={handleSuccess} trigger={<></>} />
      
      <ClientEditDialog client={editingClient} open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} onSuccess={() => {
      setIsEditDialogOpen(false);
      setEditingClient(undefined);
      refetch();
    }} />
      
      <ClientFilesDialog client={filesDialogClient} open={isFilesDialogOpen} onOpenChange={setIsFilesDialogOpen} />
      
      <ClientViewDialog client={viewingClient} open={isViewDialogOpen} onOpenChange={open => {
      setIsViewDialogOpen(open);
      if (!open) setViewingClient(undefined);
    }} />
    </div>;
}